import { addDays, differenceInDays } from 'date-fns';
import { type ProductInventoryDataFragment, ProductInventoryState } from '~~/graphql';

type Inventory = ProductInventoryDataFragment['inventory'];

const tKeys = {
    days: { product: 'delivery.days', cart: 'complete.delivery-days', confirmation: 'complete.delivery-days' },
    instant: { product: 'delivery.instant', cart: 'complete.instant', confirmation: 'complete.instant' },
    weeks: { product: 'delivery.weeks', cart: 'complete.delivery-weeks', confirmation: 'complete.delivery-weeks' },
};

export function useDeliveryEstimateText() {
    const { t, locale } = useI18n({
        useScope: 'global',
    });
    const { format: formatDate } = useLocalDate();

    // Note that this somewhat follows the logic in the elevate connector's stock amount calculations (and also consequently in our elevate UI components)
    const formatV2 = (inventory: Inventory) => {
        const { isAvailableForPurchase, expectedShipmentDate } = inventory;
        const deliveryDays = inventory.deliveryEstimateDays ?? 100;
        const isTracked = inventory.useInventoryTracking;

        const today = new Date();
        const quickDeliveryMessage = t('delivery.days', { amount: t('delivery.almost-instant-days') });
        const longDeliveryMessage = t('delivery.over-weeks', { amount: 12 });

        // Start with ruling out the situation where the product is known to not be purchaseable at all.
        if (!isAvailableForPurchase) {
            return t('delivery.out-of-stock');
        }

        // If the product uses inventory tracking and we have it in stock, the delivery is as fast as it can be - show a special message for that case.
        if (isTracked && (inventory.availableQuantity ?? 0) > 0) {
            return quickDeliveryMessage;
        }
        // If the product doesn't use inventory tracking but the estimated delivery time is really fast, then it's also considered to be in stock (by our elevate connector too). We can show the same special message as above.
        if (!isTracked && deliveryDays <= 5) {
            return quickDeliveryMessage;
        }
        // If the product uses inventory tracking and has no stock, we should have a date for the next incoming shipment (if it's coming). We display this to the customer.
        if (isTracked && expectedShipmentDate) {
            const date = new Date(expectedShipmentDate);
            // If the expected date is way in the future, show a less exact message so that it won't seem funny or scare customers away.
            if (differenceInDays(date, today) > 84) {
                return longDeliveryMessage;
            }
            // TODO
            return t('delivery.coming-to-stock', { date: formatDate(date) });
        }
        // If the product doesn't use inventory tracking, it should have a customer delivery lead time (if it's available). We still want to show a similar "coming to our stock" date to the customer - we can do this by subtracting a few days from the amount of days it's expected to take to arrive to the customer, that should reflect the date we'll have the product in our own stock.
        if (!isTracked && deliveryDays) {
            // We subtract a few days from the customer delivery estimate to get the time the product roughly should be in our own warehouse. 3 is a good number here (at least in Finland), since the AX connector uses 3 days as the default delivery time estimate for in-stock products, and 4 for products that need to first arrive to our warehouse from vendor - 3 is a safe bet.
            const daysToAdd = Math.max(deliveryDays - 3, 0);
            const date = addDays(today, daysToAdd);
            // See similar case above
            if (differenceInDays(date, today) > 84) {
                return longDeliveryMessage;
            }
            return t('delivery.coming-to-stock', { date: formatDate(date) });
        }
        // All other situations are considered to be out of stock due to missing info on the product - `isAvailableForPurchase` should be false for them anyway so we probably never hit this case.
        return t('delivery.out-of-stock');
    };

    return {
        format: (inventory: Inventory, context: 'product' | 'cart' | 'confirmation') => {
            // New simpler logic for FI
            if (locale.value === 'fi') {
                return formatV2(inventory);
            }

            const days = inventory.deliveryEstimateDays ?? undefined;
            const inventoryState = inventory.state ?? undefined;
            const availableQuantity = inventory.availableQuantity ?? undefined;

            // Straight from warehouse -> always fast, estimate days not needed
            if (availableQuantity && availableQuantity > 0) {
                return t(tKeys.days[context], { amount: t('delivery.almost-instant-days') });
            }

            // Straightforward
            if (inventoryState === 'OUT_OF_STOCK') {
                return t('delivery.out-of-stock');
            }

            // Delivery days not defined - instant / fast
            if (days === undefined) {
                return t(tKeys.days[context], { amount: t('delivery.almost-instant-days') });
            }

            if (days <= 1) {
                return t(tKeys.instant[context]);
            }
            if (days === 2) {
                return t(tKeys.days[context], { amount: t('delivery.almost-instant-days') });
            }
            if (days <= 7) {
                return t(tKeys.days[context], { amount: t('delivery.bit-more-days') });
            }
            if (days <= 12) {
                return t(tKeys.weeks[context], { amount: '1-2' });
            }
            if (days <= 19) {
                return t(tKeys.weeks[context], { amount: '2-3' });
            }
            if (days <= 26) {
                return t(tKeys.weeks[context], { amount: '3-4' });
            }
            if (days <= 33) {
                return t(tKeys.weeks[context], { amount: '4-5' });
            }
            if (days <= 40) {
                return t(tKeys.weeks[context], { amount: '5-6' });
            }
            if (days <= 47) {
                return t(tKeys.weeks[context], { amount: '6-7' });
            }
            if (days <= 54) {
                return t(tKeys.weeks[context], { amount: '7-8' });
            }
            if (days <= 61) {
                return t(tKeys.weeks[context], { amount: '8-9' });
            }
            if (days <= 68) {
                return t(tKeys.weeks[context], { amount: '9-10' });
            }
            if (days <= 75) {
                return t(tKeys.weeks[context], { amount: '10-11' });
            }
            if (days <= 82) {
                return t(tKeys.weeks[context], { amount: '11-12' });
            }
            return t('delivery.over-12-weeks');
        },

        // Used in product list / search contexts where full inventory info isn't available
        formatDays: (days: number | undefined) => {
            // Currently we don't have info about stock status in elevate, but
            // only a handful of products that *aren't* out of stock have
            // undefined delivery times, so we're fairly safe displaying a bit
            // pessimistic but not completely out-of-stock text here. Sticking
            // to earlier logic.
            if (days === undefined) {
                return t('delivery.over-4-weeks');
            }

            if (days <= 2) {
                return t('delivery-feed.in-stock');
            }
            if (days <= 7) {
                return t('delivery-feed.fast');
            }
            if (days <= 12) {
                return t('delivery-feed.1-2-weeks');
            }
            if (days <= 19) {
                return t('delivery-feed.2-3-weeks');
            }
            if (days <= 42) {
                return t('delivery-feed.4-6-weeks');
            }
            if (days <= 62) {
                return t('delivery-feed.6-8-weeks');
            }
            return t('delivery-feed.over-10-weeks');
        },
    };
}
